import { Home } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import Unauthorized from "layouts/Unauthorized";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BoxLogModel, BoxModel, MILLISECONDS } from "types";
import { canManageSite, dateTimeFormat, getBoxLabel } from "utils";

export default function BoxLogs() {
  const [{ userData }] = useContextController();
  const t = useTranslation();
  const [box, setBox] = useState<BoxModel>();
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [page, setPage] = useState(1);
  const [autoScroll, setAutoScroll] = useState(true);
  const { id } = useParams();

  const { isLoading, error, callFetch: callFetchGet } = useFetch<{ boxLogs?: BoxLogModel[] }>("GET");

  const { isLoading: loadingBox, callFetch: getBox } = useFetch<{ box?: BoxModel }>("GET");

  const [logsAll, setLogs] = useState<BoxLogModel[]>();
  const logs = useMemo(
    () =>
      logsAll?.sort((a, b) => {
        let one = new Date(a.createdAt || 0);
        let two = new Date(b.createdAt || 0);
        return one > two ? 1 : one < two ? -1 : 0;
      }),
    [logsAll]
  );

  const divRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const logsRef = useRef<BoxLogModel[]>();

  const scrollToBottom = useCallback(() => {
    if (autoScroll && divRef.current) {
      divRef.current.scrollTo({ top: divRef.current.scrollHeight });
    }
  }, [autoScroll]);

  const loadMore = useCallback((newLogs: BoxLogModel[]) => {
    const logs = logsRef.current;
    if (logs) {
      const filtered = newLogs.filter((item) => !Boolean(logs.find((item2) => item2._id === item._id)));
      if (filtered.length > 0) {
        setLogs([...logs, ...filtered]);
      }
    } else {
      setLogs(newLogs);
    }
  }, []);

  const loadOld = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append("limit", "99");
      searchParams.append("page", page + 1 + "");
      searchParams.append("boxId", id + "");
      const params = searchParams.toString();
      const response = await callFetchGet(ENDPOINTS.boxLogs + "?" + params);
      const boxLogs = response.data?.boxLogs;
      if (boxLogs) {
        if (logs) {
          setLogs([...boxLogs, ...logs]);
        } else {
          setLogs(boxLogs);
        }
        setCanLoadMore(boxLogs.length >= 99);
        setPage((prevPage) => prevPage + 1);
      } else {
        setCanLoadMore(false);
      }
    } catch (err) {
      console.error(err);
    }
  }, [page, id, callFetchGet, logs]);

  useEffect(() => {
    logsRef.current = logs;
    scrollToBottom();
  }, [logs, scrollToBottom]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
    }

    const getData = async () => {
      try {
        const searchParams = new URLSearchParams();
        searchParams.append("limit", "99");
        searchParams.append("page", "1");
        searchParams.append("boxId", id + "");
        const response = await callFetchGet(ENDPOINTS.boxLogs + "?" + searchParams.toString());
        const logs = response.data;

        if (logs?.boxLogs) {
          loadMore(logs?.boxLogs);
        }
      } catch (err) {
        console.error(err);
      }
    };

    getData();

    timeoutRef.current = setInterval(() => {
      getData();
    }, MILLISECONDS.SECOND * 30);

    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    };
  }, [callFetchGet, id, loadMore]);

  useEffect(() => {
    getBox(ENDPOINTS.boxes + "/" + id)
      .then((res) => {
        const box = res.data?.box;
        if (!box) {
          throw new Error("Invalid Box");
        }
        setBox(box);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [id, getBox]);

  return (
    <PrivateLayout canAccess={() => true}>
      {box && !canManageSite(userData, box.siteId || box.site?._id) ? (
        <Unauthorized />
      ) : (
      <Fragment>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton LinkComponent={Link} {...{ to: "/" }}>
              <Home />
            </IconButton>
          </Tooltip>
          <Button LinkComponent={Link} {...{ to: "/boxes" }}>
            {t["Boxes"]}
          </Button>
          {box?._id ? (
            <Typography color="text.disabled">{getBoxLabel(box)}</Typography>
          ) : loadingBox ? (
            <CircularProgress size="11px" />
          ) : (
            <Typography color="error.main">
              {error ? error.message || JSON.stringify(error) : t["Box not found"]}
            </Typography>
          )}
          <Typography color="text.disabled">{t["Logs"]}</Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1}></Stack>
      </Box>
      <Box width="100%" height="85vh" overflow="hidden">
        <Card
          ref={divRef}
          onScroll={(e) => {
            const myDiv = e.currentTarget;
            setAutoScroll(myDiv.scrollTop + myDiv.clientHeight >= myDiv.scrollHeight);
          }}
          sx={{
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            pb: 10,
            backgroundColor: "white",
            color: "secondary.main"
          }}
        >
          <CardContent>
            {logs && logs?.length > 0 ? (
              <Fragment>
                {canLoadMore ? (
                  <Box textAlign="center">
                    {isLoading ? <CircularProgress /> : <Button onClick={loadOld}>Load Older</Button>}
                  </Box>
                ) : (
                  <Typography textAlign="center" variant="subtitle1" fontStyle="italic" color="text.disabled">
                    {t["No More Records"]}
                  </Typography>
                )}
                {logs.map((item, i) => (
                  <Stack
                    key={item._id}
                    direction="row"
                    spacing={1}
                    width="100%"
                    justifyContent="space-between"
                    sx={({ palette }) => ({
                      ...(i !== logs.length - 1 && {
                        borderBottom: "1px solid " + palette.secondary.light,
                        marginBottom: 1
                      })
                    })}
                  >
                    {item.createdAt && <Typography color="primary.main">{dateTimeFormat(item.createdAt)}</Typography>}
                    <Typography flex={1}>{item.text}</Typography>
                  </Stack>
                ))}
                {isLoading && (
                  <Box textAlign="center">
                    <CircularProgress />
                  </Box>
                )}
              </Fragment>
            ) : isLoading ? (
              <Skeleton width="100%" height="84vh" />
            ) : (
              <Typography color="text.error">{t["No Logs found"]}</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      </Fragment>
      )}
    </PrivateLayout>
  );
}
